import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import authService from "../../Api/authService";

// Get initial authentication state from localStorage
const token = localStorage.getItem("token");
const userInfo = localStorage.getItem("userInfo");

const initialState = {
  userProfile: null, // Always start as null, will be loaded in App.js
  additionalFields: [],
  isAuthenticated: !!token, // Set to true if token exists
  isLoading: false,
  isSuccess: false,
  isError: false,
  error: null,
};

export const getProfile = createAsyncThunk(
  "user/get_profile",
  async (_, thunkAPI) => {
    try {
      return await authService.getProfile();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const authenticateUser = createAsyncThunk(
  "user/authenticate",
  async (data, thunkAPI) => {
    try {
      return await authService.authenticateUser(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const requestDeviceVerificationOTP = createAsyncThunk(
  "user/request_device_verification_otp",
  async (data, thunkAPI) => {
    try {
      return await authService.requestDeviceVerificationOTP(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getLoginVerification = createAsyncThunk(
  "user/login_verification",
  async (data, thunkAPI) => {
    try {
      return await authService.getLoginVerification(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

export const getAdditionalFields = createAsyncThunk(
  "user/additional_fields",
  async (_, thunkAPI) => {
    try {
      return await authService.getAdditionalFields();
    } catch (error) {
      return thunkAPI.rejectWithValue(error);
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    resetUserState: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.error = null;
    },
    setAuthenticated: (state, action) => {
      state.isAuthenticated = action.payload;
    },
    logout: (state) => {
      state.userProfile = null;
      state.isAuthenticated = false;
      state.additionalFields = [];
      localStorage.removeItem("token");
      localStorage.removeItem("userInfo");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.userProfile = action?.payload?.data;
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.error = null;
        state.isAuthenticated = true;

        // Store in localStorage for persistence
        if (action?.payload?.data) {
          localStorage.setItem("userInfo", JSON.stringify(action.payload.data));
        }
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.isAuthenticated = false;
      })
      .addCase(authenticateUser.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.error = null;
      })
      .addCase(authenticateUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.error = null;
      })
      .addCase(authenticateUser.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
      })
      .addCase(requestDeviceVerificationOTP.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.error = null;
      })
      .addCase(requestDeviceVerificationOTP.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.error = null;
      })
      .addCase(requestDeviceVerificationOTP.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
      })
      .addCase(getLoginVerification.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.error = null;
      })
      .addCase(getLoginVerification.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.error = null;
        state.isAuthenticated = true;
      })
      .addCase(getLoginVerification.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
        state.isAuthenticated = false;
      })
      .addCase(getAdditionalFields.pending, (state) => {
        state.isLoading = true;
        state.isSuccess = false;
        state.isError = false;
        state.error = null;
      })
      .addCase(getAdditionalFields.fulfilled, (state, action) => {
        state.additionalFields = action?.payload?.data;
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.error = null;
      })
      .addCase(getAdditionalFields.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.isSuccess = false;
      });
  },
});

export const { resetUserState, setAuthenticated, logout } = userSlice.actions;
export default userSlice.reducer;
